import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getAllUsers, deleteUser } from "../services/api";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";

const ITEMS_PER_PAGE = 10;

function AdminUserList() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [userToDelete, setUserToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "lastName",
    direction: "asc",
  });

  // États pour la modal de suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const currentUserEmail = localStorage.getItem("userEmail");

  const fetchUsers = useCallback(() => {
    setLoading(true);
    getAllUsers()
      .then((res) => setUsers(res.data))
      .catch((err) => {
        console.error("Erreur lors du chargement des utilisateurs:", err);
        setError("Impossible de récupérer les utilisateurs.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const sortedUsers = useMemo(() => {
    const sortableUsers = [...users];
    sortableUsers.sort((a, b) => {
      if (!a[sortConfig.key] || !b[sortConfig.key]) return 0;

      const aKey = String(a[sortConfig.key]).toLowerCase();
      const bKey = String(b[sortConfig.key]).toLowerCase();
      return sortConfig.direction === "asc"
        ? aKey.localeCompare(bKey)
        : bKey.localeCompare(aKey);
    });
    return sortableUsers;
  }, [users, sortConfig]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return sortedUsers.filter(
      ({ email = "", firstName = "", lastName = "" }) =>
        email.toLowerCase().includes(term) ||
        firstName.toLowerCase().includes(term) ||
        lastName.toLowerCase().includes(term)
    );
  }, [searchTerm, sortedUsers]);

  const adminCount = useMemo(
    () => users.filter((u) => u.isAdmin).length,
    [users]
  );

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Ouvrir la modal de confirmation
  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Fermer la modal de confirmation
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  // Fonction pour effectuer la suppression
  const handleDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);

    try {
      await deleteUser(userToDelete._id);
      toast.success("Utilisateur supprimé avec succès");
      fetchUsers();
      closeDeleteModal();
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
      toast.error("Erreur lors de la suppression de l'utilisateur.");
    } finally {
      setIsDeleting(false);
    }
  };

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const displayedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const SortableHeader = ({ label, columnKey }) => (
    <th
      role="button"
      onClick={() => requestSort(columnKey)}
      className="user-select-none"
    >
      {label}
      {sortConfig.key === columnKey && (
        <i
          className={`bi ms-2 ${
            sortConfig.direction === "asc" ? "bi-arrow-up" : "bi-arrow-down"
          }`}
        />
      )}
    </th>
  );

  return (
    <div className="container py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4">
          Liste des utilisateurs{" "}
          <span className="badge bg-secondary">{filteredUsers.length}</span>
        </h2>
        <div>
          <Link className="btn btn-primary btn-sm me-2" to="/admin/create">
            <i className="bi bi-person-plus"></i> Ajouter utilisateur
          </Link>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={fetchUsers}
          >
            <i className="bi bi-arrow-clockwise"></i> Actualiser
          </button>
        </div>
      </div>

      <div className="input-group mb-3 shadow-sm">
        <span className="input-group-text bg-white">
          <i className="bi bi-search"></i>
        </span>
        <input
          className="form-control"
          placeholder="Rechercher par email, prénom ou nom..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-2">Chargement des utilisateurs...</p>
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !filteredUsers.length && (
        <div className="text-center py-5">
          <i className="bi bi-search fs-1 text-muted"></i>
          <p className="text-muted mt-2">
            {searchTerm
              ? "Aucun utilisateur ne correspond à votre recherche."
              : "Aucun utilisateur trouvé."}
          </p>
        </div>
      )}

      {!loading && filteredUsers.length > 0 && (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover bg-white shadow-sm">
              <thead>
                <tr>
                  <SortableHeader label="Email" columnKey="email" />
                  <SortableHeader label="Prénom" columnKey="firstName" />
                  <SortableHeader label="Nom" columnKey="lastName" />
                  <th>Statut</th>
                  <th>Email vérifié</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedUsers.map((user) => {
                  const isCurrentUser = user.email === currentUserEmail;
                  const disableDelete =
                    (user.isAdmin && adminCount <= 1) || isCurrentUser;
                  const disableEdit = isCurrentUser;

                  return (
                    <tr key={user._id}>
                      <td>{user.email}</td>
                      <td>{user.firstName}</td>
                      <td>{user.lastName}</td>
                      <td>
                        <span
                          className={`badge ${
                            user.isAdmin ? "bg-success" : "bg-secondary"
                          }`}
                        >
                          {user.isAdmin ? "Admin" : "Utilisateur"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            user.isEmailVerified ? "bg-success" : "bg-warning"
                          }`}
                        >
                          {user.isEmailVerified ? "Vérifié" : "Non vérifié"}
                        </span>
                      </td>
                      <td className="text-end">
                        <Link
                          className={`btn btn-sm btn-primary me-2 ${
                            disableEdit ? "disabled" : ""
                          }`}
                          to={!disableEdit ? `/admin/edit/${user._id}` : "#"}
                          title={
                            disableEdit
                              ? "Impossible de modifier votre compte"
                              : "Modifier"
                          }
                        >
                          <i className="bi bi-pencil-square"></i>
                        </Link>

                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => openDeleteModal(user)}
                          disabled={disableDelete}
                          title={
                            isCurrentUser
                              ? "Impossible de supprimer votre compte"
                              : disableDelete
                              ? "Impossible de supprimer le dernier admin"
                              : "Supprimer"
                          }
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <nav aria-label="Page navigation">
              <ul className="pagination justify-content-center">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    aria-label="Previous"
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </button>
                </li>

                {[...Array(totalPages)].map((_, idx) => (
                  <li
                    key={idx}
                    className={`page-item ${
                      currentPage === idx + 1 ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(idx + 1)}
                    >
                      {idx + 1}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    aria-label="Next"
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}

      {/* Modal de confirmation de suppression */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmation de suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userToDelete && (
            <>
              <div className="text-center mb-3">
                <i className="bi bi-exclamation-triangle-fill text-danger fs-1"></i>
              </div>
              <p className="mb-2">
                Êtes-vous sûr de vouloir supprimer définitivement l'utilisateur
                :
              </p>
              <p className="fw-bold mb-3 text-center">
                {userToDelete.firstName} {userToDelete.lastName} (
                {userToDelete.email})
              </p>
              <div className="alert alert-danger">
                <i className="bi bi-info-circle-fill me-2"></i>
                Cette action est irréversible et supprimera définitivement
                toutes les données associées à cet utilisateur.
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeDeleteModal}>
            <i className="bi bi-x-circle me-1"></i>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Suppression en cours...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-1"></i>
                Confirmer la suppression
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminUserList;
